import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SearchResultsModel } from '../models/search-results-model';

@Injectable({
  providedIn: 'root'
})
export class ResultSelectionService {

  private allSlelectedSubject = new BehaviorSubject<boolean>(false);
  public allSelected$ = this.allSlelectedSubject.asObservable();

  private allExceptSubject = new BehaviorSubject<boolean>(false);
  public allExcept$ = this.allExceptSubject.asObservable();

  private selectedObjectsSubject = new BehaviorSubject<SearchResultsModel[]>([]);
  public selectedArray$ = this.selectedObjectsSubject.asObservable();

  private excludedObjectsSubject = new BehaviorSubject<SearchResultsModel[]>([]);
  public excludedArray$ = this.excludedObjectsSubject.asObservable();

  private itemCountSubject = new BehaviorSubject<number>(0);
  public itemCount$ = this.itemCountSubject.asObservable();

  private selectedCountSubject = new BehaviorSubject<number>(0);
  public selectedCount$ = this.selectedCountSubject.asObservable();

  private allSelectedWithLegacySubject = new BehaviorSubject<boolean>(false);
  public allSelectedWithLegacy$ = this.allSelectedWithLegacySubject.asObservable();

  private downloadableCountSubject = new BehaviorSubject<number>(0);
  public downloadableCount$ = this.downloadableCountSubject.asObservable();

  constructor() { }

  setAllSelected(data: boolean) {
    this.allSlelectedSubject.next(data);
  }

  isAllSelected() {
    return this.allSlelectedSubject.value;
  }

  private getSelectedArray() {
    return this.selectedObjectsSubject.value;
  }

  private setSelectedArray(items: SearchResultsModel[]) {
    this.selectedObjectsSubject.next(items);
  }

  addToSelectedArray(item: SearchResultsModel) {
    if (!this.isAllExcept()) {
      let items = this.getSelectedArray();
      items.push(item);
      this.setSelectedArray(items);
    }

    this.removeFromExcludedArray(item);
    this.getCount();
  }

  removeFromSelectedArray(item: SearchResultsModel) {
    let items = this.getSelectedArray();
    let results = items.filter(i => i.document.AccessionNumber != item.document.AccessionNumber);
    this.setSelectedArray(results);
    this.getCount();
  }

  setAllExcept(isTrue: boolean) {
    this.allExceptSubject.next(isTrue);
  }

  isAllExcept() {
    return this.allExceptSubject.value;
  }

  private getExcludedArray() {
    return this.excludedObjectsSubject.value;
  }

  private setExcludedArray(items: SearchResultsModel[]) {
    this.excludedObjectsSubject.next(items);
  }

  addToExcludedArray(item: SearchResultsModel) {
    let items = this.getExcludedArray();
    items.push(item);
    this.setExcludedArray(items);
    this.removeFromSelectedArray(item);
    this.getCount();
  }

  removeFromExcludedArray(item: SearchResultsModel) {
    let items = this.getExcludedArray();
    let results = items.filter(i => i.document.AccessionNumber != item.document.AccessionNumber);
    this.setExcludedArray(results);
    this.getCount();
  }

  clearSelected() {
    this.setSelectedArray([]);
    this.getCount();
  }

  clearExcluded() {
    this.setExcludedArray([]);
    this.getCount();
  }

  clearAll() {
    this.setAllExcept(false);
    this.setAllSelected(false);
    this.clearSelected();
    this.clearExcluded();
  }

  setCount(count: number) {
    this.itemCountSubject.next(count);
  }

  getCount(): number {
    let selectedCount = this.getSelectedArray().length;

    // If All selected then the count is all the items from the results.
    if (this.isAllSelected()) {
      selectedCount = this.itemCountSubject.value;
    }

    // if this is allExcept then the count is the total - excpetions
    if (this.isAllExcept()) {
      selectedCount = (this.itemCountSubject.value - this.getExcludedArray().length);
    }

    this.selectedCountSubject.next(selectedCount);
    return selectedCount;
  }

  getSelected(): SearchResultsModel[] {
    return this.getSelectedArray();
  }

  getExcluded(): SearchResultsModel[] {
    return this.getExcludedArray();
  }

  isItemSelected(item: SearchResultsModel): boolean {
    if (this.isAllSelected()) {
      return true;
    }

    const items = this.getSelectedArray();
    const exItems = this.getExcludedArray();

    const incl = items.some(i => i.document.AccessionNumber == item.document.AccessionNumber);
    const ex = exItems.some(e => e.document.AccessionNumber == item.document.AccessionNumber);

    if (incl) {
      return true;
    }

    if (this.isAllExcept() && !ex) {
      return true;
    }

    return false;
  }

  setallSelectedWithLegacy(items: SearchResultsModel[]) {
    const legacy = items.some(l => l.document.ItemType == 'legacy');

    if (legacy) {
      this.allSelectedWithLegacySubject.next(true);
    }
    else {
      this.allSelectedWithLegacySubject.next(false);
    }
  }

  legacyItemsSelected(): boolean {
    if(this.isAllSelected()) {
      return this.allSelectedWithLegacySubject.value;
    }

    const items = this.getSelectedArray();

    const legacyItems = items.some(i => i.document.ItemType == 'legacy');
    
    if (legacyItems) {
      return true;
    }

    return false;
  }

  setDownloadableCount(count: number) {
    this.downloadableCountSubject.next(count);
  }

}
